/**
 * Turns raw microphone blocks into pitch frames, incrementally.
 *
 * The AudioWorklet hands over 4096-sample blocks at the context's rate. Getting from
 * there to frames the scorer can use has three details that are easy to get wrong and
 * silent when you do, which is why it lives in one place used by both the solo studio
 * and the singing room:
 *
 *   1. Resample to exactly 16 kHz. Rounding 44100/16000 to 3 yields a 14.7 kHz stream
 *      labelled 16 kHz and every pitch reads ~1.5 semitones flat.
 *   2. Keep the band-pass state across blocks. Re-creating the filter per block puts a
 *      transient at every boundary — 12 a second for YIN to trip over.
 *   3. Carry the leftover samples between blocks. Dropping the remainder loses a
 *      fraction of a hop each time and the take drifts steadily ahead of the reference.
 *
 * YIN runs against the absolute energy floor only. The adaptive gate needs the whole
 * take and is applied afterwards by `applyAdaptiveGate`, which can only ever remove
 * frames — so what comes out of here is a superset, exactly as `trackFrames` intends.
 */
import {
  TRACK, downsampleTo16k, createBandpass, createDetector, frameEnergy, hzToMidi,
} from './pitchTrack.mjs';

export class FrameTracker {
  constructor(sourceRate) {
    this.sourceRate = sourceRate;
    this.detector = createDetector();
    this.reset();
  }

  reset() {
    this.bandpass = createBandpass(TRACK.sampleRate);
    this.pending = new Float32Array(0);
    this.count = 0;
    this.peak = 0;
  }

  /**
   * @param block Float32Array of microphone samples at the source rate
   * @returns the frames produced by this block (also useful for feeding a meter)
   */
  push(block) {
    if (!block || !block.length) return [];

    for (let i = 0; i < block.length; i += 1) {
      const v = block[i] < 0 ? -block[i] : block[i];
      if (v > this.peak) this.peak = v;
    }

    const filtered = this.bandpass(downsampleTo16k(block, this.sourceRate));
    const merged = new Float32Array(this.pending.length + filtered.length);
    merged.set(this.pending, 0);
    merged.set(filtered, this.pending.length);

    const { frame, hop, sampleRate, energyFloor, fMin, fMax } = TRACK;
    const out = [];
    let offset = 0;
    while (offset + frame <= merged.length) {
      const slice = merged.subarray(offset, offset + frame);
      const energy = frameEnergy(slice);
      let midi = null;
      if (energy > energyFloor) {
        const hz = this.detector(slice);
        if (hz && hz > fMin && hz < fMax) midi = hzToMidi(hz);
      }
      out.push({ t: (this.count * hop) / sampleRate, midi, energy });
      this.count += 1;
      offset += hop;
    }
    this.pending = merged.slice(offset);
    return out;
  }
}
